import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/** Higgsfield-style sticky chapter: pinned photo + editorial overlay. */
export const CinematicChapter = ({
  chapterNum,
  chapterLabel,
  headline,
  italic,
  body,
  image,
  video,          // optional — if provided, renders <video> instead of <img>
  poster,         // optional poster for video
  scrubVideo = false, // if true, video.currentTime is driven by scroll (frame-by-frame)
  align = "left",
  stats,
}) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const imgScale = useTransform(scrollYProgress, [0, 1], [1.18, 1.02]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.15, 0.7, 1], [0.7, 0.5, 0.5, 0.85]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.06, 0.35, 0.7, 1], [0, 1, 1, 1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.35, 0.7, 1], ["6%", "0%", "0%", "-8%"]);

  // Scroll-scrub video: map scrollYProgress → video.currentTime
  useEffect(() => {
    if (!scrubVideo || !videoRef.current) return;
    const v = videoRef.current;
    v.pause(); // never auto-play; scroll drives it

    const syncFromProgress = () => {
      if (!v.duration || isNaN(v.duration)) return;
      const p = scrollYProgress.get();
      const t = Math.min(1, Math.max(0, (p - 0.05) / 0.9));
      v.currentTime = t * v.duration;
    };

    // Re-sync as soon as metadata is available (handles late-loading video)
    v.addEventListener("loadedmetadata", syncFromProgress);
    const unsub = scrollYProgress.on("change", syncFromProgress);
    return () => {
      unsub();
      v.removeEventListener("loadedmetadata", syncFromProgress);
    };
  }, [scrubVideo, scrollYProgress]);

  const alignJustify = align === "right" ? "justify-end" : "justify-start";
  const alignText = align === "right" ? "text-right items-end" : "text-left items-start";
  // Give scroll-scrubbed chapters more scroll length for smoother playback
  const sectionHeight = scrubVideo ? "h-[420vh]" : "h-[260vh]";

  return (
    <section ref={ref} className={`relative ${sectionHeight} w-full bg-black`}>
      <div className="sticky top-0 h-screen w-full overflow-hidden cinematic-grain cinematic-vignette">
        {video ? (
          <motion.video
            ref={videoRef}
            src={video}
            poster={poster}
            autoPlay={!scrubVideo}
            muted
            loop={!scrubVideo}
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ scale: imgScale, y: imgY }}
          />
        ) : (
          <motion.img
            src={image}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ scale: imgScale, y: imgY }}
          />
        )}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20"
          style={{ opacity: overlayOpacity }}
        />

        <motion.div
          className={`absolute inset-0 flex items-end ${alignJustify}`}
          style={{ opacity: textOpacity, y: textY }}
        >
          <div className={`container-premium pb-20 md:pb-32 flex flex-col ${alignText}`}>
            <div className="max-w-3xl">
              {/* Eyebrow: mono num — hairline — CAPS label */}
              <div className={`flex items-center gap-4 mb-8 ${align === "right" ? "justify-end" : ""}`}>
                {chapterNum && <span className="chapter-num">{chapterNum}</span>}
                <span className="h-px w-12 bg-[#C4A570]/60" />
                <span className="chapter-label">{chapterLabel}</span>
              </div>

              {/* Headline */}
              <WordReveal
                headline={headline}
                italic={italic}
                className="cinematic-serif text-[#F5F1EA] text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.98]"
              />

              {body && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  className="cinematic-body mt-8 max-w-md text-[15px] md:text-base"
                >
                  {body}
                </motion.p>
              )}

              {stats && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1, delay: 0.9 }}
                  className={`mt-12 grid grid-cols-3 gap-10 border-t border-[#C4A570]/25 pt-6 max-w-md ${
                    align === "right" ? "ml-auto" : ""
                  }`}
                >
                  {stats.map((s, i) => (
                    <div key={i}>
                      <div className="cinematic-serif text-[#C4A570] text-2xl md:text-3xl">
                        {s.value}
                      </div>
                      <div className="chapter-label mt-2 text-[9px]">{s.label}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/** Word-by-word blur-in reveal */
const WordReveal = ({ headline, italic, className }) => {
  const words = headline.split(" ");
  const italicWords = italic ? italic.split(" ") : [];
  return (
    <h2 className={className}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.2 + i * 0.07, ease: [0.2, 0.65, 0.3, 0.9] }}
          className="inline-block mr-[0.22em]"
        >
          {w}
        </motion.span>
      ))}
      {italic && (
        <>
          <br />
          {italicWords.map((w, i) => (
            <motion.span
              key={`i-${i}`}
              initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.9,
                delay: 0.2 + (words.length + i) * 0.07,
                ease: [0.2, 0.65, 0.3, 0.9],
              }}
              className="cinematic-serif-italic inline-block mr-[0.22em]"
            >
              {w}
            </motion.span>
          ))}
        </>
      )}
    </h2>
  );
};

export default CinematicChapter;
